/**
 * OSMD Render Coordinator
 * 
 * Batches OSMD render operations to prevent rapid sequential renders
 * that can make the browser unresponsive. Shows visual countdown timer
 * when renders are pending.
 */
export class OSMDRenderCoordinator {
  constructor(osmdRef, options = {}) {
    this.osmdRef = osmdRef;
    this.delay = options.delay || 1500; // Default 1.5 seconds
    this.onTimerUpdate = options.onTimerUpdate || null; // Callback for timer updates
    this.onRenderStart = options.onRenderStart || null; // Callback when render starts
    this.onRenderComplete = options.onRenderComplete || null; // Callback when render completes
    
    // State tracking
    this.isRendering = false;
    this.pendingRender = false;
    this.timeoutId = null;
    this.startTime = null;
    this.timerIntervalId = null;
    this.isBatchMode = false; // For settings loading
  }

  /**
   * Start batch mode - accumulates render requests without timer
   * Used during settings loading to batch all changes
   */
  startBatch() {
    this.isBatchMode = true;
    this.cancelPendingRender();
  }

  /**
   * End batch mode and execute single render
   * Used after settings loading is complete
   */
  endBatch() {
    this.isBatchMode = false;
    if (this.pendingRender) {
      this.executeRender();
    } else {
      // If no render was needed during batch, still fire completion callback
      if (this.onRenderComplete) {
        setTimeout(() => {
          this.onRenderComplete();
        }, 0);
      }
    }
  }

  /**
   * Request a render operation
   * @param {Object} options - Options for the render request
   * @param {string} options.priority - 'immediate' for urgent renders, 'normal' for batched
   * @param {string} options.reason - Reason for the render (for debugging)
   */
  requestRender(options = {}) {
    const { priority = 'normal', reason = 'unknown' } = options;

    // If currently rendering, queue the request
    if (this.isRendering) {
      this.pendingRender = true;
      return;
    }

    // In batch mode, just mark as pending without timer
    if (this.isBatchMode) {
      this.pendingRender = true;
      return;
    }

    // For immediate priority, render right away
    if (priority === 'immediate') {
      this.pendingRender = true; // CRITICAL: Set pendingRender before executeRender
      this.executeRender();
      return;
    }

    // Normal priority - start countdown timer
    this.pendingRender = true;
    this.startCountdownTimer();
  }

  /**
   * Start the visual countdown timer
   */
  startCountdownTimer() {
    // Cancel any existing timer
    this.cancelPendingRender();

    this.startTime = Date.now();
    
    // Notify that timer started
    if (this.onTimerUpdate) {
      this.onTimerUpdate({
        isActive: true,
        timeRemaining: this.delay,
        progress: 0
      });
    }

    // Start the countdown interval for visual updates
    this.timerIntervalId = setInterval(() => {
      const elapsed = Date.now() - this.startTime;
      const remaining = Math.max(0, this.delay - elapsed);
      const progress = elapsed / this.delay;

      if (this.onTimerUpdate) {
        this.onTimerUpdate({
          isActive: true,
          timeRemaining: remaining,
          progress: Math.min(1, progress)
        });
      }

      // Timer expired
      if (remaining <= 0) {
        this.clearTimerInterval();
        this.executeRender();
      }
    }, 50); // Update every 50ms for smooth countdown

    // Set main timeout as backup
    this.timeoutId = setTimeout(() => {
      this.clearTimerInterval();
      this.executeRender();
    }, this.delay);
  }

  /**
   * Cancel pending render and timer
   */
  cancelPendingRender() {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
    
    this.clearTimerInterval();
    
    if (this.onTimerUpdate) {
      this.onTimerUpdate({
        isActive: false,
        timeRemaining: 0,
        progress: 0
      });
    }
  }

  /**
   * Clear the timer interval
   */
  clearTimerInterval() {
    if (this.timerIntervalId) {
      clearInterval(this.timerIntervalId);
      this.timerIntervalId = null;
    }
  }

  /**
   * Execute the actual render
   */
  executeRender() {
    
    if (!this.pendingRender || this.isRendering) {
      return;
    }

    // Check if osmdRef is valid
    if (!this.osmdRef || !this.osmdRef.current) {
      // Don't spam console - only warn if this is the first time
      if (!this._hasWarnedAboutNullRef) {
        this._hasWarnedAboutNullRef = true;
      }
      this.handleRenderComplete(new Error('OSMD reference is null'));
      return;
    }

    // Store local reference to prevent race conditions
    const osmd = this.osmdRef.current;
    if (!osmd) {
      if (!this._hasWarnedAboutNullRef) {
        this._hasWarnedAboutNullRef = true;
      }
      this.handleRenderComplete(new Error('OSMD instance is null'));
      return;
    }

    // Clear timer state
    this.cancelPendingRender();
    
    // Set rendering state
    this.isRendering = true;
    this.pendingRender = false;

    // Notify render start
    if (this.onRenderStart) {
      this.onRenderStart();
    }

    try {
      // Execute the OSMD render using local reference
      
      
      const renderResult = osmd.render();
      
      // Handle both sync and async render results
      Promise.resolve(renderResult)
        .then(() => {
          this.handleRenderComplete();
        })
        .catch((error) => {
          console.error('OSMDRenderCoordinator: Render failed:', error);
          this.handleRenderComplete(error);
        });
    } catch (error) {
      console.error('OSMDRenderCoordinator: Render threw exception:', error);
      this.handleRenderComplete(error);
    }
  }

  /**
   * Handle render completion
   */
  handleRenderComplete(error = null) {
    this.isRendering = false;

    // Notify render complete
    if (this.onRenderComplete) {
      this.onRenderComplete(error);
    }

    // If another render was requested while we were rendering, execute it
    // But only if we have a valid osmdRef and we're not in batch mode
    if (this.pendingRender && !this.isBatchMode && this.osmdRef && this.osmdRef.current) {
      // Use immediate priority for follow-up renders to avoid long delays
      setTimeout(() => {
        if (this.pendingRender && this.osmdRef && this.osmdRef.current) {
          this.executeRender();
        }
      }, 0);
    } else if (this.pendingRender && (!this.osmdRef || !this.osmdRef.current)) {
      // Clear pending flag if osmdRef is gone
      this.pendingRender = false;
    }
  }

  /**
   * Force immediate render (bypasses timer)
   * Use sparingly - mainly for critical updates
   */
  forceRender() {
    this.cancelPendingRender();
    this.pendingRender = true;
    this.executeRender();
  }

  /**
   * Check if coordinator is busy
   */
  isBusy() {
    return this.isRendering || this.pendingRender;
  }

  /**
   * Cleanup - call when component unmounts
   */
  destroy() {
    this.cancelPendingRender();
    this.isRendering = false;
    this.pendingRender = false;
    this.osmdRef = null;
    this.onTimerUpdate = null;
    this.onRenderStart = null;
    this.onRenderComplete = null;
  }
}